import React, { useState, useEffect, useCallback } from "react";
import "./generateuniqueid.scss";
import Header from "../../component/loaderComponent/Header/Header";
import Loader from "../../component/loaderComponent/loaderComponent/Loader";
import Sidebar from "../Sidebar";
import { useDispatch } from "react-redux";
import {
  getPartCategories,
  getCapColorCodes,
  generateUniqueIds
} from "../../actions/generateUniqueIdAction";
import { notify } from '../../services/notification';
import { HiMenu, HiX } from "react-icons/hi";
import useMobileView from "../../constants/useMobileView";
import { useNavigate } from "react-router-dom";

function GenerateUniqueId() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSidebarOpen, toggleSidebar } = useMobileView();

  const [isLoading, setIsLoading] = useState(false);
  const [partCategory, setPartCategory] = useState(null);
  const [capColorCode, setCapColorCode] = useState(null);
  const [specialCode, setSpecialCode] = useState("");
  const [numberOfCodes, setNumberOfCodes] = useState(0);
  const [partCategoryOptions, setPartCategoryOptions] = useState([]);
  const [capColorCodeOptions, setCapColorCodeOptions] = useState([]);

  useEffect(() => {
    const fetchPartCategories = async () => {
      setIsLoading(true);
      const categories = await dispatch(getPartCategories());
      if (categories?.length) {
        const options = categories.map((category) => ({
          value: category.id,
          label: `${category.part_category_code}-${category.part_category_name}`
        }));
        setPartCategoryOptions(options);
      }
      setIsLoading(false);
    };
    fetchPartCategories();
  }, [dispatch]);

  useEffect(() => {
    const fetchCapColorCodes = async () => {
      if (!partCategory?.value) return;
      setIsLoading(true);
      const colorCodes = await dispatch(getCapColorCodes(partCategory.value));
      if (colorCodes?.length) {
        const options = colorCodes.map((color) => ({
          value: color.id,
          label: `${color.cap_colour_code}(${color.cap_colour_name})`
        }));
        setCapColorCodeOptions(options);
      }
      setIsLoading(false);
    };
    fetchCapColorCodes();
  }, [partCategory, dispatch]);

  const handleSelectChange = (e, options, setValue, resetCallback) => {
    const selected = options.find(
      (option) => option.value === parseInt(e.target.value)
    );
    setValue(selected || null);
    if (resetCallback) resetCallback();
  };

  const handleSpecialCodeChange = useCallback((e) => {
    setSpecialCode(e.target.value.trimStart()); // No leading spaces
  }, []);

  const handleNumberOfCodesChange = useCallback((e) => {
    const value = e.target.value.trim(); // Trim spaces
    setNumberOfCodes(parseInt(value) || 0);
  }, []);

  const handleSubmit = async () => {
    if (!partCategory || !capColorCode || numberOfCodes <= 0) {
      notify.error("Please fill all required fields");
      return;
    }

    const data = {
      part_category_id: partCategory.value,
      cap_color_id: capColorCode.value,
      number_of_codes: numberOfCodes
    };

    // Add only if Special Code is not empty after trimming
    if (specialCode.trim() !== "") {
      data.special_code = specialCode.trim();
    }

    setIsLoading(true);
    const response = await dispatch(generateUniqueIds(data));
    setIsLoading(false);
    navigate("/uniqueidgenerationlist");

    if (response?.success) {
      setSpecialCode("");
      setPartCategory(null);
      setCapColorCode(null);
      setNumberOfCodes("");
    }
  };

  const handleCancel = () => {
    navigate("/uniqueidgenerationlist");
  };

  return (
    <div className="generate-unique-id-container">
      {/* Mobile Header */}
      <div className="uniqueid-header">
        <button className="uniqueid-hamburger-button" onClick={toggleSidebar}>
          {isSidebarOpen ? (
            <HiX className="uniqueid-icon" size={24} />
          ) : (
            <HiMenu className="uniqueid-icon" size={24} />
          )}
        </button>
      </div>
      {/* Right Content */}
      <div className="generate-unique-id-containerright">
        <Header title="Generate Unique IDs for TRU4 QR codes" />
        <div className="generate-unique-id">
          {isLoading ? (
            <Loader />
          ) : (
            <div className="form-div">
              {/* Part Category */}
              <div className="grid-unique-id">
                <div>
                  <label className="label-generate-uniqueid">Part Category</label>
                  <div className="message-text">
                    <span className="label-generate-uniqueid-info">
                      Search and select a part Category.
                    </span>
                  </div>
                </div>
                <select
                  className="input-select"
                  value={partCategory?.value || ""}
                  onChange={(e) =>
                    handleSelectChange(
                      e,
                      partCategoryOptions,
                      setPartCategory,
                      () => setCapColorCode(null) // Reset cap color
                    )
                  }
                >
                  <option value="">-- Select Part Category --</option>
                  {partCategoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cap Colour Code */}
              <div className="grid-unique-id">
                <div>
                  <label className="label-generate-uniqueid">Cap Colour Code</label>
                  <div className="message-text">
                    <span className="label-generate-uniqueid-info">
                      Select cap color code, related to above part category.
                    </span>
                  </div>
                </div>
                <select
                  className="input-select"
                  value={capColorCode?.value || ""}
                  onChange={(e) =>
                    handleSelectChange(e, capColorCodeOptions, setCapColorCode)
                  }
                  disabled={!partCategory}
                >
                  <option value="">-- Select Cap Colour Code --</option>
                  {capColorCodeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Special Code */}
              <div className="grid-unique-id">
                <div>
                  <label className="label-generate-special-code">Special Code</label>
                  <div className="message-text">
                    <span className="label-generate-uniqueid-info">
                      Type a special code to use with generation process
                    </span>
                  </div>
                </div>
                <input
                  className="input-field"
                  placeholder="Optional, and can be empty."
                  type="text"
                  value={specialCode}
                  onChange={handleSpecialCodeChange}
                />
              </div>

              {/* Number of Codes */}
              <div className="grid-unique-id">
                <label className="label-generate-uniqueid">
                  How Many Unique IDs To Generate?
                </label>
                <input
                  className="input-field"
                  type="text"
                  value={numberOfCodes}
                  onChange={handleNumberOfCodesChange}
                />
              </div>

              {/* Submit Button */}
              <div className="btn-generate">
                <button className="generate-button" onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="generate-button"
                  onClick={handleSubmit}
                  disabled={!capColorCode || !numberOfCodes || !partCategory}
                >
                  {isLoading ? "Generating..." : "Generate Unique IDs"}
                </button>
              </div>

              {/* Note */}
              <div className="note">
                <h4>Note</h4>
                <ul>
                  <li>
                    To avoid quota expiry per second, it's better to generate 1
                    large batch of categories per 24 hours.
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GenerateUniqueId;
